import React, { useMemo } from 'react';
import { Box, Typography, Button, Alert, Stack } from '@mui/material';
import { Add as AddIcon, Help as HelpIcon } from '@mui/icons-material';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Page, Selection, Sort } from '@syncfusion/ej2-react-grids';
import { useAppSelector } from '../../../../../../app/store';
import { selectCurrentFeeSchedule } from '../../../../../../services/ContractService';
import { GrouperView } from '../../../../../../services/ContractService/types/contractTypes';
import { useSignalR } from '../../../../../../hooks/useSignalR';
import { AddEditGrouperDialog, GrouperHelpDialog, GrouperUploadProgress } from '../../../dialogs';
import { GrouperUploadCsv, GrouperDownloadCsv } from '../../../commands/contractCommands';
import { useGrouperDialog, useBaseRatesSelection, useGrouperUpload } from './hooks';
import { rateTemplate, perDiemTemplate } from './templates';

export interface BaseRatesGridProps {
  testId?: string;
}

/**
 * BaseRatesGrid - Displays and manages base rate groupers for a fee schedule
 *
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Custom hooks for dialog, selection, and upload handling
 * - SignalR integration for upload progress
 * - Clean component structure
 */
export const BaseRatesGrid: React.FC<BaseRatesGridProps> = ({ testId }) => {
  const selectedFeeSchedule = useAppSelector(selectCurrentFeeSchedule);
  const groupers = selectedFeeSchedule?.groupers || [];

  // Use custom hooks
  const { dialogOpen, editingGrouper, helpDialogOpen, handleAddGrouper, handleEditGrouper, handleCloseDialog, handleOpenHelpDialog, handleCloseHelpDialog } =
    useGrouperDialog();
  const { handleRowSelection } = useBaseRatesSelection(groupers, handleEditGrouper);
  
  const {
    uploadProgress,
    isUploadInProgress,
    onProgress,
    onCompleted,
    onError,
    handleCloseProgressDialog,
    handleStartUpload,
  } = useGrouperUpload();

  // SignalR connection for upload progress
  const { isConnected: isSignalRConnected } = useSignalR({
    serviceName: 'Contracts',
    uploadType: 'Grouper',
    onProgress,
    onCompleted,
    onError,
    autoConnect: true,
  });

  // Transform data for grid display
  const gridData = useMemo(
    () =>
      groupers?.map((grouper: GrouperView) => ({
        id: grouper.id,
        code: grouper.code,
        rate: grouper.rate,
        isPerDiem: grouper.isPerDiem,
      })) || [],
    [groupers]
  );

  const gridSettings = {
    allowPaging: true,
    allowSorting: true,
    pageSettings: { pageSize: 15, pageSizes: [15, 25, 50] },
    selectionSettings: { type: 'Single' as const, mode: 'Row' as const },
    rowHeight: 32,
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}
        >
          Base Rates ({groupers.length})
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddGrouper}
            size="small"
          >
            Add Grouper
          </Button>
          <GrouperUploadCsv
            disabled={isUploadInProgress}
            onUploadStart={handleStartUpload}
          />
          <GrouperDownloadCsv disabled={groupers.length === 0} />
          <Button variant="outlined" startIcon={<HelpIcon />} onClick={handleOpenHelpDialog} size="small">
            Help
          </Button>
        </Stack>
      </Stack>

      {groupers.length > 0 && (
        <Typography
          variant="body2"
          sx={{ color: '#64748b', fontStyle: 'italic', mb: 1, fontSize: '0.75rem' }}
        >
          Click on any row to edit or delete a grouper
        </Typography>
      )}

      {groupers.length === 0 ? (
        <Alert severity="info">
          No groupers defined yet. Click "Add Grouper" to create your first base rate.
        </Alert>
      ) : (
        <Box
          sx={{
            border: '1px solid #e2e8f0',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <GridComponent
            dataSource={gridData}
            {...gridSettings}
            height="350"
            width="1000"
            rowSelected={handleRowSelection}
            style={{ border: 'none' }}
            data-testid={testId}
          >
            <ColumnsDirective>
              <ColumnDirective field="code" headerText="Code" width="100" textAlign="Left" />
              <ColumnDirective
                field="rate"
                headerText="Rate"
                width="150"
                textAlign="Right"
                template={rateTemplate}
              />
              <ColumnDirective
                field="isPerDiem"
                headerText="Per Diem"
                width="120"
                textAlign="Center"
                template={perDiemTemplate}
              />
            </ColumnsDirective>
            <Inject services={[Page, Selection, Sort]} />
          </GridComponent>
        </Box>
      )}

      <AddEditGrouperDialog
        open={dialogOpen}
        editingGrouper={editingGrouper}
        onClose={handleCloseDialog}
      />

      <GrouperHelpDialog open={helpDialogOpen} onClose={handleCloseHelpDialog} />

      {uploadProgress && (
        <GrouperUploadProgress
          open={!!uploadProgress}
          progress={uploadProgress?.progressPercentage || 0}
          status={uploadProgress?.message || 'Preparing upload...'}
          error={uploadProgress?.status === 'Failed' ? uploadProgress.message : undefined}
          success={uploadProgress?.progressPercentage === 100}
          uploadCount={uploadProgress?.processedRecords}
          totalCount={uploadProgress?.totalRecords}
          onClose={handleCloseProgressDialog}
        />
      )}
    </Box>
  );
};

export default BaseRatesGrid;
